import React, { useState, useMemo } from 'react';
import { cn, toDisplayString } from '@/lib/utils';
import { getCellValue, compareValues } from '@/lib/table-helpers';
import { renderFilterInput, renderCellContent } from '@/lib/table-ui-helpers';
import { Spinner } from './Spinner';
import { Button } from './Button';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon, ChevronLeftIcon, ChevronRightIcon } from './icons';

// ============================================================================
// Type Definitions
// ============================================================================

export interface FilterConfig {
  type: 'text' | 'select';
  placeholder?: string;
  options?: { label: string; value: string }[];
}

export interface ColumnDef<T> {
  id: string;
  header: string;
  accessor: keyof T | ((row: T) => unknown);
  cell?: (value: unknown, row: T) => React.ReactNode;
  sortable?: boolean;
  filter?: FilterConfig;
  width?: string;
  align?: 'left' | 'center' | 'right';
  /**
   * Hide the column below this Tailwind breakpoint. Header, filter cell, and
   * body cell are all hidden together. Filters on hidden columns still apply
   * via URL/state — only the rendering is suppressed.
   */
  hideBelow?: 'sm' | 'md' | 'lg';
}

interface SortState {
  columnId: string | null;
  direction: 'asc' | 'desc';
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

export interface ServerPaginationConfig {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  keyExtractor: (row: T) => string | number;
  isLoading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  enablePagination?: boolean;
  enableFilters?: boolean;
  className?: string;
  /** When provided, pagination is server-driven. Client-side slicing and sorting are disabled. */
  serverPagination?: ServerPaginationConfig;
  /** When provided, filter changes call this callback instead of filtering client-side. Use with serverPagination. */
  onFilterChange?: (filters: Record<string, string>) => void;
}

// ============================================================================
// DataTable Component
// ============================================================================

function DataTableInner<T>(
  {
    data,
    columns,
    keyExtractor,
    isLoading = false,
    error = null,
    emptyMessage = 'No data found.',
    pageSizeOptions = [10, 25, 50],
    defaultPageSize = 10,
    enablePagination = true,
    enableFilters = true,
    className,
    serverPagination,
    onFilterChange,
  }: DataTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  // State
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortState>({ columnId: null, direction: 'asc' });
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Filter data (skip when server-driven via onFilterChange)
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (onFilterChange) return data; // server handles filtering
    
    return data.filter((row) => {
      // Safety check for columns
      if (!columns) return true;

      return columns.every((col) => {
        // Safety check for col
        if (!col) return true;

        const filterValue = filters[col.id];
        if (!filterValue || !col.filter) return true;

        const cellValue = getCellValue(row, col.accessor);
        const cellStr = toDisplayString(cellValue).toLowerCase();
        const filterStr = filterValue.toLowerCase();

        if (col.filter.type === 'text') {
          return cellStr.includes(filterStr);
        }
        if (col.filter.type === 'select') {
          return toDisplayString(cellValue) === filterValue;
        }
        return true;
      });
    });
  }, [data, columns, filters, onFilterChange]);


  // Sort data (skip when server-driven)
  const sortedData = useMemo(() => {
    if (serverPagination) return filteredData;
    if (!sort.columnId) return filteredData;

    const column = columns.find((col) => col.id === sort.columnId);
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = getCellValue(a, column.accessor);
      const bVal = getCellValue(b, column.accessor);
      return compareValues(aVal, bVal, sort.direction);
    });
  }, [filteredData, columns, sort, serverPagination]);

  // Paginate data (skip when server-driven — data is already the right page)
  const paginatedData = useMemo(() => {
    if (serverPagination) return sortedData;
    if (!enablePagination) return sortedData;
    const start = pagination.pageIndex * pagination.pageSize;
    return sortedData.slice(start, start + pagination.pageSize);
  }, [sortedData, pagination, enablePagination, serverPagination]);

  // Pagination calculations
  const effectiveTotal = serverPagination ? serverPagination.total : sortedData.length;
  const effectivePageSize = serverPagination ? serverPagination.pageSize : pagination.pageSize;
  const effectivePageIndex = serverPagination ? serverPagination.page - 1 : pagination.pageIndex;
  const totalPages = Math.ceil(effectiveTotal / effectivePageSize);
  const canPreviousPage = effectivePageIndex > 0;
  const canNextPage = effectivePageIndex < totalPages - 1;

  // Handlers
  const handleFilterChange = (columnId: string, value: string) => {
    const newFilters = { ...filters, [columnId]: value };
    setFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    } else {
      setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page
    }
  };

  const handleSort = (columnId: string) => {
    setSort((prev) => {
      if (prev.columnId === columnId) {
        return { columnId, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { columnId, direction: 'asc' };
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    if (serverPagination) {
      serverPagination.onPageSizeChange(newSize);
    } else {
      setPagination({ pageIndex: 0, pageSize: newSize });
    }
  };

  const handlePreviousPage = () => {
    if (serverPagination) {
      serverPagination.onPageChange(serverPagination.page - 1);
    } else {
      setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }));
    }
  };

  const handleNextPage = () => {
    if (serverPagination) {
      serverPagination.onPageChange(serverPagination.page + 1);
    } else {
      setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
    }
  };

  // Render sort icon
  const renderSortIcon = (column: ColumnDef<T>) => {
    if (!column.sortable) return null;

    if (sort.columnId === column.id) {
      return sort.direction === 'asc' ? (
        <ChevronUpIcon className="h-4 w-4" />
      ) : (
        <ChevronDownIcon className="h-4 w-4" />
      );
    }
    return <ChevronUpDownIcon className="h-4 w-4 text-muted-foreground/50" />;
  };

  // Alignment classes
  const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  // Responsive visibility class for breakpoint-aware columns
  const getHideBelowClass = (hideBelow?: 'sm' | 'md' | 'lg') => {
    if (!hideBelow) return '';
    if (hideBelow === 'sm') return 'hidden sm:table-cell';
    if (hideBelow === 'md') return 'hidden md:table-cell';
    return 'hidden lg:table-cell';
  };

  // Loading state
  if (isLoading) {
    return (
      <div ref={ref} className={cn('flex items-center justify-center py-12', className)}>
        <Spinner size="lg" />
        <span className="ml-3 text-muted-foreground">Loading...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div ref={ref} className={cn('rounded-lg border border-error-200 bg-error-50 p-4', className)}>
        <p className="text-sm font-medium text-error-700">Error: {error}</p>
      </div>
    );
  }

  // Empty state (no data at all)
  if (!data || data.length === 0) {
    return (
      <div ref={ref} className={cn('py-12 text-center', className)}>

        <p className="text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  const hasFilters = enableFilters && columns.some((col) => col.filter);

  return (
    <div ref={ref} className={cn('w-full', className)}>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse">
          <thead>
            {/* Header row */}
            <tr className="bg-muted/50 border-b border-border">
              {columns.map((column) => (
                <th
                  key={column.id}

                  className={cn(
                    'px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider',
                    getAlignmentClass(column.align),
                    getHideBelowClass(column.hideBelow),
                    column.width
                  )}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(column.id)}
                      className={cn(
                        'inline-flex items-center gap-1 hover:text-foreground transition-colors',
                        sort.columnId === column.id && 'text-foreground'
                      )}
                    >
                      {column.header}
                      {renderSortIcon(column)}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>

            {/* Filter row */}
            {enableFilters && hasFilters && (
              <tr className="bg-muted/30 border-b border-border">
                {columns.map((column) => (
                  <th
                    key={`filter-${column.id}`}
                    className={cn('px-3 py-1.5', getHideBelowClass(column.hideBelow))}
                  >
                    {column.filter
                      ? renderFilterInput(column.filter, column.id, filters, handleFilterChange)
                      : null}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                  No matching results found.
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => (
                <tr
                  key={keyExtractor(row)}
                  className="border-b border-border hover:bg-muted/30 transition-colors"
                >
                  {columns.map((column) => {
                    const value = getCellValue(row, column.accessor);
                    return (
                      <td
                        key={column.id}
                        className={cn(
                          'px-3 py-1.5 text-sm text-foreground',
                          getAlignmentClass(column.align),
                          getHideBelowClass(column.hideBelow),
                          column.width
                        )}
                      >
                        {renderCellContent(column, value, row)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(enablePagination || serverPagination) && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-background">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page:</span>
            <select
              value={effectivePageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="px-2 py-1 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              {effectiveTotal === 0
                ? '0 results'
                : `${effectivePageIndex * effectivePageSize + 1}-${Math.min(
                    (effectivePageIndex + 1) * effectivePageSize,
                    effectiveTotal
                  )} of ${effectiveTotal}`}
            </span>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={!canPreviousPage}
                aria-label="Previous page"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!canNextPage}
                aria-label="Next page"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ForwardRef wrapper with generic support
export const DataTable = React.forwardRef(DataTableInner) as <T>(
  props: DataTableProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement;

